import { useState } from "react";
import { ProjectAnnotation } from "../../../backend/Projects/types";
import CoursableIcons from "../../../utils/CoursableIcons";
import MarkdownView from "../../elements/MarkdownView";
import { useProjectManager } from "./Hooks/useProjectManager";

export const removeLegacyCitations = (content: string, annotations: ProjectAnnotation[]) =>
  annotations.reduce((cleanContent, annotation) => (annotation.citation ? cleanContent.split(annotation.citation).join("") : cleanContent), content);

export const deduplicateProjectAnnotations = (annotations: ProjectAnnotation[]): ProjectAnnotation[] => {
  const sources = new Map<string, ProjectAnnotation>();

  annotations.forEach((annotation) => {
    const existing = sources.get(annotation.fileID);
    if (!existing) {
      sources.set(annotation.fileID, { ...annotation });
      return;
    }

    sources.set(annotation.fileID, {
      ...annotation,
      ...existing,
      filename: existing.filename || annotation.filename,
      citation: existing.citation || annotation.citation,
      quote: existing.quote || annotation.quote,
    });
  });

  return Array.from(sources.values());
};

interface ProjectSourcesPanelProps {
  annotations: ProjectAnnotation[];
}

export const ProjectSourcesPanel = ({ annotations }: ProjectSourcesPanelProps) => {
  const projectManager = useProjectManager();
  const [expandedSourceID, setExpandedSourceID] = useState<string | undefined>(undefined);
  const sources = deduplicateProjectAnnotations(annotations);

  if (sources.length === 0) return null;

  return (
    <div className="not-prose mt-4 w-full border-t border-systemGray-300 pt-3" data-testid="project-sources-panel">
      <div className="mb-2 flex items-center gap-1 text-mini font-semibold uppercase tracking-wide text-systemGray-400">{CoursableIcons.Info()} Sources</div>
      <ol className="flex flex-col gap-1.5">
        {sources.map((source, sourceIndex) => {
          const sourceName = source.filename || projectManager?.Files.files.find((file) => file.openaiID === source.fileID)?.name || "Source";
          const hasQuote = Boolean(source.quote?.trim());
          const expanded = expandedSourceID === source.fileID;

          return (
            <li key={source.fileID} className="rounded-md border border-systemGray-300 bg-systemGray-100/50 px-3 py-2 text-sm">
              <button
                type="button"
                className={`flex w-full items-center gap-2 text-left ${hasQuote ? "cursor-pointer" : "cursor-default"}`}
                disabled={!hasQuote}
                aria-expanded={hasQuote ? expanded : undefined}
                onClick={() => hasQuote && setExpandedSourceID(expanded ? undefined : source.fileID)}
              >
                <span className="text-mini text-systemGray-400">{sourceIndex + 1}.</span>
                <span className="min-w-0 grow truncate font-medium text-foreground">{sourceName}</span>
                {hasQuote && CoursableIcons.Chevron("down", `text-mini text-systemGray-400 duration-150 ${expanded ? "rotate-180" : ""}`)}
              </button>
              {hasQuote && expanded && <p className="mt-2 whitespace-pre-wrap border-t border-systemGray-300 pt-2 text-sm text-systemGray-600">{source.quote}</p>}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

interface MarkdownWithAnnotationsProps {
  annotations: ProjectAnnotation[];
  children: string | null | undefined;
  size?: "default" | "sm" | null;
  className?: string;
}

export const MarkdownWithAnnotations = ({ annotations, children, className, size }: MarkdownWithAnnotationsProps) => {
  const cleanContent = removeLegacyCitations(children ?? "", annotations);

  return (
    <div className="w-full">
      <MarkdownView className={className} size={size}>
        {cleanContent}
      </MarkdownView>
      <ProjectSourcesPanel annotations={annotations} />
    </div>
  );
};
